import { ref } from 'vue';
import { SeoMetaDataFilterVariations } from './SeoMetaDataFilterVariations';
import { ISubscriber } from '@studyportals/event-aggregation-service-interface';
import { EventAggregationService } from '../../platform/EventAggregationService';
import { Store } from '../../store/Store';
import { FilterSelection } from '../../filters/selection/FilterSelection';
import { injectable } from 'inversify';
import { ConvergingTask } from '../../platform/ConvergingTask';
import { ISeoMetaDataInfo } from './ISeoMetaDataInfo';
import { SeoMetaDataInput } from './SeoMetaDataInput';
import { EmbeddedStatePropagatedEvent } from '@/url/events/EmbeddedStatePropagatedEvent';
import { SortingSelection } from '@/sorting/SortingSelection';

@injectable()
export class SeoMetaData
	extends ConvergingTask<ISeoMetaDataInfo, SeoMetaDataInput>
	implements ISubscriber<EmbeddedStatePropagatedEvent>
{
	private readonly eventAggregationService: EventAggregationService;
	private readonly seoFilterVariations: SeoMetaDataFilterVariations;
	private readonly store: Store;

	public readonly title = ref('');
	public readonly description = ref('');

	public constructor(
		eventAggregationService: EventAggregationService,
		seoFilterVariations: SeoMetaDataFilterVariations,
		filterSelection: FilterSelection,
		sortSelection: SortingSelection,
		store: Store
	) {
		super();

		this.eventAggregationService = eventAggregationService;
		this.seoFilterVariations = seoFilterVariations;
		this.store = store;

		void this.refresh({
			degree: store.getters.getDegree(),
			filterSelection: filterSelection.state,
			sortSelection: sortSelection.state
		});

		this.eventAggregationService.subscribeTo(EmbeddedStatePropagatedEvent.EventType, this);
	}

	public notify(event: EmbeddedStatePropagatedEvent): void {
		if (EmbeddedStatePropagatedEvent.is(event)) {
			void this.refresh({
				degree: event.state.degreeOrNull ?? this.store.getters.getDegree(),
				filterSelection: event.state.filterSelectionState,
				sortSelection: event.state.sortingState
			});
		}
	}

	private async refresh(input: SeoMetaDataInput): Promise<void> {
		await this.execute(input);
	}

	protected override async run(input: SeoMetaDataInput): Promise<ISeoMetaDataInfo> {
		return await this.seoFilterVariations.handle(input.degree, input.filterSelection, input.sortSelection);
	}

	protected override commit(result: ISeoMetaDataInfo): void {
		this.title.value = result.title;
		this.description.value = result.description;
	}

	protected override queued(): boolean {
		return false;
	}
}
